import { Pagination } from 'antd';
import { BriefcaseBusiness, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import {
  useGetAllOfferLettersQuery,
  useGetAppliedJobsQuery,
} from '../../../app/features/recruitment/recruitment.api';
import PageHero from '../../../components/PageHero';
import useAuth from '../../../hooks/useAuth';
import WebLayout from '../../../layouts/WebLayout';
import cn from '../../../lib/cn';
import { PATHS } from '../../../routes/paths';

/* ----------------------------------------------------------------------------
 * Shared pieces for the Job Portal pages (openings, details, applied jobs,
 * offer letters) — same visual language as the home and legal pages.
 * -------------------------------------------------------------------------- */

export const panelClass = 'rounded-2xl border border-gray-200 bg-white';

/* ================================== UI ================================== */

const AVATAR_TONES = [
  'bg-theme-primary/10 text-theme-primary',
  'bg-indigo-50 text-indigo-600',
  'bg-emerald-50 text-emerald-600',
  'bg-amber-50 text-amber-600',
  'bg-rose-50 text-rose-600',
  'bg-violet-50 text-violet-600',
];

/** Organization monogram with a stable tint per name. */
export const OrgAvatar = ({
  name,
  size = 'md',
}: {
  name?: string | null;
  size?: 'md' | 'lg';
}) => {
  const label = (name || '?').trim();
  const tone =
    AVATAR_TONES[
      [...label].reduce((sum, ch) => sum + ch.charCodeAt(0), 0) %
        AVATAR_TONES.length
    ];
  return (
    <span
      aria-hidden
      className={cn(
        'flex shrink-0 items-center justify-center rounded-xl font-semibold uppercase ring-1 ring-inset ring-black/[0.04]',
        size === 'lg' ? 'h-14 w-14 text-xl' : 'h-11 w-11 text-base',
        tone,
      )}
    >
      {label.charAt(0)}
    </span>
  );
};

export const MetaItem = ({
  icon: Icon,
  children,
}: {
  icon: LucideIcon;
  children: ReactNode;
}) => (
  <span className="inline-flex items-center gap-1.5 text-[13px] text-gray-600">
    <Icon className="h-3.5 w-3.5 text-gray-400" strokeWidth={2} />
    {children}
  </span>
);

export const EmptyState = ({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  action?: ReactNode;
}) => (
  <div
    className={cn(
      panelClass,
      'flex flex-col items-center px-6 py-16 text-center',
    )}
  >
    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-theme-primary/10 text-theme-primary">
      <Icon className="h-5 w-5" strokeWidth={1.75} />
    </span>
    <h3 className="mt-4 text-base font-semibold text-gray-900">{title}</h3>
    <p className="mt-1 max-w-sm text-sm leading-6 text-gray-500">
      {description}
    </p>
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const PortalPagination = ({
  page,
  limit,
  total,
  onChange,
}: {
  page: number;
  limit: number;
  total: number;
  onChange: (page: number, limit: number) => void;
}) => {
  if (total <= limit) return null;
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  return (
    <div className="mt-8 flex flex-col items-center justify-between gap-3 border-t border-gray-200 pt-6 sm:flex-row">
      <p className="text-sm text-gray-500">
        Showing <span className="font-medium text-gray-900">{start}</span>–
        <span className="font-medium text-gray-900">{end}</span> of{' '}
        <span className="font-medium text-gray-900">{total}</span>
      </p>
      <Pagination
        className="[&_.ant-pagination-item-active]:!border-theme-primary [&_.ant-pagination-item-active]:!bg-white [&_.ant-pagination-item-active_a]:!font-semibold [&_.ant-pagination-item-active_a]:!text-theme-primary [&_.ant-pagination-item_a]:text-gray-600"
        current={page}
        pageSize={limit}
        total={total}
        showSizeChanger={false}
        onChange={(next, size) => {
          onChange(next, size);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />
    </div>
  );
};

/* ================================ Shell ================================= */

const TABS = [
  { key: 'openings', label: 'Job openings', path: PATHS.jobPortal.root },
  { key: 'applied', label: 'Applied jobs', path: PATHS.jobPortal.applications },
  { key: 'offers', label: 'Offer letters', path: PATHS.jobPortal.offers },
] as const;

type TabKey = (typeof TABS)[number]['key'];

/** Job openings / Applied jobs / Offer letters — for signed-in applicants. */
const PortalTabs = ({ active }: { active: TabKey }) => {
  const navigate = useNavigate();
  const { verifiedInfo } = useAuth();
  const applicantId = verifiedInfo?.id as string;

  const { data: applied } = useGetAppliedJobsQuery(
    { applicant_id: applicantId, limit: 1 },
    { skip: !applicantId },
  );
  const { data: offers } = useGetAllOfferLettersQuery(
    { applicant_id: applicantId, limit: 1 },
    { skip: !applicantId },
  );
  const counts: Partial<Record<TabKey, number>> = {
    applied: applied?.data?.meta?.total,
    offers: offers?.data?.meta?.total,
  };

  return (
    <nav className="-mb-px flex gap-6 overflow-x-auto">
      {TABS.map((tab) => {
        const isActive = tab.key === active;
        const count = counts[tab.key];
        return (
          <button
            key={tab.key}
            onClick={() => navigate(tab.path)}
            aria-current={isActive ? 'page' : undefined}
            className={cn(
              'relative flex shrink-0 items-center gap-2 pb-3.5 text-sm font-medium transition-colors',
              isActive ? 'text-gray-900' : 'text-gray-500 hover:text-gray-900',
            )}
          >
            {tab.label}
            {count !== undefined && (
              <span
                className={cn(
                  'rounded-full px-1.5 py-px text-[11px] font-semibold tabular-nums',
                  isActive
                    ? 'bg-theme-primary/10 text-theme-primary'
                    : 'bg-gray-100 text-gray-500',
                )}
              >
                {count}
              </span>
            )}
            {isActive && (
              <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-theme-primary" />
            )}
          </button>
        );
      })}
    </nav>
  );
};

/** Page frame: hero header (+ tabs when signed in) and a content area. */
export const PortalShell = ({
  tab,
  title,
  description,
  children,
}: {
  tab: TabKey;
  title: string;
  description: string;
  children: ReactNode;
}) => {
  const { verifiedInfo } = useAuth();
  return (
    <WebLayout>
      <PageHero
        badge={{ icon: BriefcaseBusiness, label: 'Job Portal' }}
        title={title}
        description={description}
        className="max-w-6xl"
        footer={verifiedInfo ? <PortalTabs active={tab} /> : undefined}
      />
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8 lg:py-12">
        {children}
      </div>
    </WebLayout>
  );
};
